import Spinner from '@/components/atoms/Spinner'
import { useAppDispatch, useAppSelector } from '@/store/hooks'
import Container from '@/components/atoms/Container'
import Button from '@/components/atoms/Button'
import Icon from '@/components/atoms/Icon'
import { twMerge } from 'tailwind-merge'
import classNames from 'classnames'
import { contentCreators } from '@/store/ducks/content'
import { useEffect, useRef } from 'react'

import './style.css'
import './prism.css'
import libraryService from '@/services/libraryService'
import { useNavigate } from 'react-router-dom'
import { cohortsRepository } from '@/repositories'

export function List() {
	const myRef = useRef(null)
	const dispatch = useAppDispatch()
	const navigate = useNavigate()

	const {
		auth: { user },
		content: { moduleOpened },
		matriculate: { classSelect },
	} = useAppSelector((state) => state)

	const {
		execute: getUnitsRecorded,
		data: unitsData,
		isSuccess: isSuccessUnits,
		isLoading: isLoadingUnits,
	} = libraryService.getUnitsRecordedMutation()

	const {
		execute: getLesson,
		data: lessonData,
		isLoading: isLoadingLesson,
	} = libraryService.getLessonMutation()

	const {
		execute: completeTopic,
		isLoading: isLoadingCompleteTopic,
		isSuccess: isSuccessCompleteTopic,
	} = libraryService.completeTopic()

	const { data: progressData, isLoading: isLoadingProgress } =
		libraryService.getProgress()

	// Encontra o onboarding específico
	const findOnboarding = user?.onboarding_all?.find(
		(onboarding) => onboarding.curso_id === classSelect?.curso_id
	)

	useEffect(() => {
		if (isSuccessCompleteTopic)
			dispatch(cohortsRepository.util.invalidateTags(['Cohorts']))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessCompleteTopic])

	// Função para carregar o primeiro tópico incompleto
	function loadFirstIncompleteTopic() {
		if (!unitsData) return
		for (const unit of unitsData) {
			const firstIncompleteTopic = unit.topicos.find(
				(topic) => !progressData?.concluidos?.includes(topic.id)
			)
			if (firstIncompleteTopic) {
				getLesson({ topicId: firstIncompleteTopic.id })
				handleUnit(unit.id)
				break
			}
		}
	}

	// useEffect que carrega o primeiro tópico incompleto se as unidades forem carregadas com sucesso
	useEffect(() => {
		if (isSuccessUnits && unitsData) {
			loadFirstIncompleteTopic()
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isSuccessUnits, unitsData]) // Removeu o eslint-disable aqui

	// Função para lidar com a seleção de um tópico
	function handleTopic(topicId: number) {
		if (moduleOpened?.length > 1) dispatch(contentCreators.reset())
		getLesson({ topicId })
	}

	// Função para lidar com a seleção de uma unidade
	function handleUnit(idx: number) {
		dispatch(contentCreators.setOrRemove({ idx }))
	}

	// Atualiza as unidades abertas quando os dados da aula são recebidos
	useEffect(() => {
		if (lessonData && !moduleOpened?.includes(lessonData.unidade_id)) {
			dispatch(contentCreators.setOrRemove({ idx: lessonData.unidade_id }))
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [lessonData]) // Removeu o eslint-disable aqui também

	// Função para completar um tópico
	const handleCompleteTopic = ({ lessonId }: { lessonId: number }) => {
		completeTopic({ topicoId: lessonId })
	}

	// Função para rolar a página para o topo
	const scrollToTop = () => {
		window.scrollTo({ top: 0, behavior: 'smooth' })
	}

	// useEffect para carregar as unidades gravadas e rolar para o topo
	useEffect(() => {
		if (!classSelect?.curso_id) {
			if (user?.modo_curso_unico) navigate('/curso')

			navigate('/')
			return
		}

		const courseId = Number(classSelect?.curso_id || findOnboarding?.curso_id)

		if (courseId) {
			getUnitsRecorded({ courseId })
		}

		scrollToTop()
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [classSelect?.curso_id, findOnboarding]) // Mantido o que já funcionava

	const progressBar = Math.round(progressData?.progresso || 0)

	return (
		<div className='w-full flex flex-col pt-4'>
			<Container className='w-full !max-w-[1420px] xl:px-8 2xl:px-0'>
				<div className='relative w-full py-6'>
					{(() => {
						if (isLoadingProgress || isLoadingUnits) {
							return (
								<div className='flex justify-center'>
									<Spinner
										className={
											'inline w-10 h-10 text-gray-200 animate-spin fill-brand-primary'
										}
									/>
								</div>
							)
						}

						const arrayOfCompletedUnitIds = unitsData
							?.map((unit: { topicos: any[]; id: any }) => {
								// Verifica se a unidade tem tópicos. Se não tiver, não considera como completa.
								if (unit.topicos.length === 0) {
									return null
								}

								// Verifica se todos os tópicos da unidade estão completos
								const allTopicsCompleted = unit.topicos.every(
									(topic) =>
										topic?.id && progressData?.concluidos?.includes(topic.id)
								)

								return allTopicsCompleted ? unit.id : null
							})
							.filter((unitId) => unitId !== null)

						// const isCertificate = false

						return (
							<Container className='max-w-full px-0 sm:px-0 md:px-0 2lg:px-0 xl:px-0'>
								<div
									className={twMerge(
										classNames(
											'flex flex-col w-full 2lg:grid 2lg:grid-cols-[minmax(337px,_437px)_1fr] justify-between gap-8',
											{
												'2lg:grid-cols-1': !unitsData?.length,
											}
										)
									)}
								>
									<div
										className={twMerge(
											classNames('flex flex-col w-full gap-4', {
												hidden: !unitsData?.length,
												'opacity-30 pointer-events-none':
													unitsData?.length &&
													unitsData.every((unit) => !unit.topicos?.length),
											})
										)}
									>
										<div className='flex flex-col w-full bg-zinc-900 rounded-lg pt-5 pb-6 px-4 gap-3'>
											<span className='text-base font-bold text-zinc-50'>
												Progresso
											</span>
											<div className='flex w-full items-center gap-3'>
												<span className='text-base py-1 px-2 bg-brand-primary rounded-md font-bold text-brand-text-primary'>
													{progressBar}%
												</span>
												<div className='flex w-full bg-zinc-800 rounded-full h-2'>
													<div
														className='bg-brand-primary h-2 rounded-full transition-all duration-500'
														style={{ width: `${progressBar}%` }}
													></div>
												</div>
												{/* <Popover className='relative'>
													<Popover.Button className='focus:outline-none'>
														<span
															onClick={() =>
																isCertificate && navigate('/certificado')
															}
															className={twMerge(
																'flex gap-2 group transition-colors duration-200',
																classNames({
																	'text-brand-primary/90': isCertificate,
																})
															)}
														>
															<Icon
																name='certificate'
																width='22'
																height='22'
																className={twMerge(
																	'text-[#F7F9FB]/70 group-hover:text-[#F7F9FB] focus:text-[#F7F9FB]',
																	classNames({
																		'text-[#F7F9FB] group-hover:text-[#F7F9FB]': isCertificate,
																	})
																)}
															/>
															<span
																className={twMerge(
																	'text-base font-bold text-[#F7F9FB]/40 group-hover:text-[#F7F9FB]/80',
																	classNames({
																		'text-[#F7F9FB] group-hover:text-[#F7F9FB]': isCertificate,
																	})
																)}
															>
																Certificado
															</span>
														</span>

														<Popover.Panel className='absolute border border-[#F7F9FB]/5 bg-[#1E162C] shadow-lg rounded-md w-full top-10 min-w-[340px] pt-2 py-3 px-4 z-10'>
															<div className='flex flex-col text-start gap-2'>
																<span className='text-[15px] font-medium text-[#F7F9FB]'>
																	Você ainda não tem acesso ao <br /> certificado desse
																	curso.
																</span>
															</div>
														</Popover.Panel>
													</Popover.Button>
												</Popover> */}
											</div>
										</div>
										<div className='flex flex-col gap-3'>
											<div
												className='flex flex-col w-full bg-zinc-900 rounded-lg py-6 px-3 gap-3 overflow-auto'
												id='sidebar-content'
											>
												{(() => {
													if (isLoadingUnits || !unitsData) {
														return (
															<div className='flex justify-center'>
																<Spinner
																	className={
																		'inline w-10 h-10 text-gray-200 animate-spin fill-brand-primary'
																	}
																/>
															</div>
														)
													}

													return (
														<div className='flex flex-col gap-8 transition-all duration-1000'>
															<div className='flex flex-col gap-0'>
																{(() => {
																	if (unitsData?.length > 0) {
																		return unitsData?.map((unit, key) => {
																			const isOpen = moduleOpened.includes(
																				unit.id
																			)
																			const isCompleted =
																				arrayOfCompletedUnitIds?.length &&
																				arrayOfCompletedUnitIds?.includes(
																					unit.id
																				)

																			return (
																				<div
																					className={classNames(
																						'flex flex-col px-4 rounded-lg gap-6 last-of-type:mb-0 last-of-type:gap-0 transition-all duration-500',
																						{
																							'bg-zinc-800 py-4 mb-6 last-of-type:gap-6':
																								isOpen,
																						}
																					)}
																					key={key}
																					id={`unit-${unit.id}`}
																				>
																					<div className='flex flex-col gap-2'>
																						<div
																							className='flex group justify-between items-center cursor-pointer gap-1'
																							onClick={() =>
																								handleUnit(unit.id)
																							}
																						>
																							<div
																								key={key}
																								className='flex items-center gap-2'
																							>
																								<p
																									className={
																										'text-lg leading-[140%] text-zinc-50 font-bold'
																									}
																								>
																									{unit.nome}
																								</p>
																							</div>

																							{(() => {
																								if (isCompleted) {
																									return (
																										<div>
																											<Icon
																												name='check-content'
																												width='24'
																												height='24'
																												className='w-5 h-5 text-brand-primary'
																											/>
																										</div>
																									)
																								}

																								return (
																									<div>
																										<Icon
																											name='arrow-up'
																											width='22'
																											height='22'
																											className={twMerge(
																												'text-zinc-50 transition-transform duration-500',
																												classNames({
																													'-rotate-180': isOpen,
																												})
																											)}
																										/>
																									</div>
																								)
																							})()}
																						</div>
																					</div>
																					<div
																						className={twMerge(
																							'flex flex-col h-full transition-all duration-500 overflow-clip',
																							classNames({
																								'max-h-screen':
																									moduleOpened.includes(
																										unit?.id
																									),
																								'max-h-0':
																									!moduleOpened.includes(
																										unit?.id
																									),
																							})
																						)}
																					>
																						<div className='flex flex-col gap-3'>
																							{unit.topicos.map(
																								(topicMap, topicIdx) => {
																									const isCompleted =
																										progressData?.concluidos?.includes(
																											topicMap?.id
																										)

																									return (
																										<div
																											className='group'
																											key={topicIdx}
																											id={`topic-${topicMap.id}`}
																											onClick={() =>
																												handleTopic(topicMap.id)
																											}
																										>
																											<div
																												className={classNames(
																													'flex max-w-max py-1 rounded-md cursor-pointer transition-all duration-200',
																													{
																														'text-opacity-0':
																															lessonData?.id ===
																															topicMap.id,
																													}
																												)}
																											>
																												<div className='flex items-center gap-4'>
																													<span
																														className={classNames(
																															'flex min-w-[20px] min-h-[20px] md:min-w-[20px] md:min-h-[20px] justify-center items-center border border-[#829ABA] rounded-full',
																															{
																																'border-none':
																																	isCompleted,
																															}
																														)}
																													>
																														{isCompleted && (
																															<Icon
																																name='check-content'
																																width='20'
																																height='20'
																																className='text-brand-primary'
																															/>
																														)}
																													</span>
																													<p
																														className={twMerge(
																															classNames(
																																'flex items-center text-base font-medium text-zinc-50 group-hover:opacity-100 opacity-70 leading-[155%] gap-2',
																																{
																																	'opacity-100 font-bold':
																																		lessonData?.id ===
																																		topicMap.id,
																																}
																															)
																														)}
																													>
																														{topicMap.nome}
																													</p>
																												</div>
																											</div>
																										</div>
																									)
																								}
																							)}
																						</div>
																					</div>
																				</div>
																			)
																		})
																	}

																	return <></>
																})()}
															</div>
														</div>
													)
												})()}
											</div>
										</div>
									</div>
									{(() => {
										if (isLoadingLesson) {
											return (
												<div className='flex justify-center'>
													<Spinner
														className={
															'inline w-10 h-10 text-gray-200 animate-spin fill-brand-primary'
														}
													/>
												</div>
											)
										}

										if (lessonData) {
											const isCompleted = progressData?.concluidos?.includes(
												lessonData?.id
											)

											return (
												<div className='flex flex-col w-full'>
													<div className='flex flex-col gap-8'>
														<div className='relative overflow-clip flex flex-col w-full rounded-lg bg-zinc-900 p-4 lg:p-7'>
															<h1 className='font-bold text-xl md:text-2xl lg:text-[28px] leading-[120%] text-white z-10'>
																{lessonData?.nome}
															</h1>
														</div>
														<div className='flex flex-col gap-10'>
															<div id='prism-style'>
																<div
																	dangerouslySetInnerHTML={{
																		__html: lessonData.conteudo,
																	}}
																	ref={myRef}
																	id='content-style'
																/>
															</div>
														</div>
													</div>

													<div className='flex justify-end items-end'>
														<Button
															className={twMerge(
																'w-full max-w-[270px] h-[50px] rounded-full transition-all duration-700',
																classNames({
																	'bg-[#15ca71] dark:bg-[#15ca71] text-white dark:text-white border-none':
																		isLoadingCompleteTopic || isCompleted,
																})
															)}
															onClick={() =>
																handleCompleteTopic({
																	lessonId: lessonData.id,
																})
															}
															isLoading={isLoadingCompleteTopic}
														>
															<Icon
																name='check-circle-outline'
																width='18'
																height='18'
																className={twMerge(
																	'stroke-brand-text-primary',
																	classNames({
																		'dark:stroke-white': isCompleted,
																	})
																)}
															/>
															<p className='ml-4'>
																{isCompleted
																	? 'Tópico Completo'
																	: 'Marcar como concluído'}
															</p>
														</Button>
													</div>
												</div>
											)
										}

										return (
											<div className='w-full h-max py-4 px-4 flex items-start justify-center bg-zinc-900 border border-dashed border-white/10 rounded-xl'>
												<p className='text-base text-zinc-50 font-medium text-center'>
													Nenhuma unidade disponível no momento
												</p>
											</div>
										)
									})()}
								</div>
							</Container>
						)
					})()}
				</div>
			</Container>
		</div>
	)
}
